import React, { useEffect } from 'react'
import { Col, Row } from 'react-bootstrap'
import classNames from 'classnames'
import { Button } from '../../../components/Button/Button'
import { useCheckMigrationNeededQuery } from '../../../store/ai/meetingTemplate'
import { useAccount } from '../../../common/useAccount'
import { useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import './MeetingTypeMigrationBanner.scss'

const MEETING_TYPE_MIGRATIONS = [
  { oldName: 'Client Meeting', newName: 'General Client Check-In' },
  { oldName: 'General Meeting Summary', newName: 'Internal Meeting Summary' },
  { oldName: 'Plan Review', newName: 'Plan Proposal' },
] as const

export interface MeetingTypeMigrationBannerProps {
  collapsed: boolean
  onToggleCollapse: () => void
  onUpdateAll: () => void
  onBackUp: () => void
  isBackingUp: boolean
  onViewUpdateDetails: () => void
  onLearnHow: () => void
}

export const MeetingTypeMigrationBanner: React.FC<MeetingTypeMigrationBannerProps> = ({
  collapsed,
  onToggleCollapse,
  onUpdateAll,
  onBackUp,
  isBackingUp,
  onViewUpdateDetails,
  onLearnHow,
}) => {
  const { accountId } = useAccount()
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const { trackingClient } = useTracking()
  const { data: migrationNeeded } = useCheckMigrationNeededQuery({ accountId })

  useEffect(() => {
    if (migrationNeeded) {
      trackingClient.track({
        eventName: TrackEvent.MEETING_TEMPLATE_MIGRATION_BANNER_VIEWED,
        eventDetails: { accountId, userId: currentUserId },
      })
    }
  }, [migrationNeeded, accountId, currentUserId, trackingClient])

  if (!migrationNeeded) {
    return null
  }

  return (
    <Row className='mb-4'>
      <Col>
        <div className={classNames('meeting-type-migration-banner', { 'meeting-type-migration-banner--collapsed': collapsed })}>
          <div className='meeting-type-migration-banner__header'>
            <div className='meeting-type-migration-banner__header-icon'>
              <i className='bi bi-arrow-up-circle' />
            </div>
            <div className='meeting-type-migration-banner__header-title'>Update system meeting types for better prep and summaries</div>
            <button className='meeting-type-migration-banner__dismiss-btn' onClick={onToggleCollapse} aria-label={collapsed ? 'Expand' : 'Collapse'}>
              <i className={collapsed ? 'bi bi-chevron-expand' : 'bi bi-chevron-contract'} />
            </button>
          </div>

          <div className='meeting-type-migration-banner__body'>
            <div className='meeting-type-migration-banner__body-inner'>
              <div className='d-flex flex-column gap-3'>
                <div className='meeting-type-migration-banner__description-row'>
                  <p className='meeting-type-migration-banner__description mb-0'>
                    Three system meeting types have improved versions with clearer prep guidance and more detailed summaries.
                  </p>
                  <Button variant='grayGhost' className='button-medium' onClick={onViewUpdateDetails}>
                    View update details
                  </Button>
                </div>

                <div className='meeting-type-migration-banner__table'>
                  {MEETING_TYPE_MIGRATIONS.map((migration) => (
                    <div className='meeting-type-migration-banner__row' key={migration.oldName}>
                      <div className='meeting-type-migration-banner__cell meeting-type-migration-banner__cell--old'>{migration.oldName}</div>
                      <div className='meeting-type-migration-banner__cell meeting-type-migration-banner__cell--arrow'>
                        <i className='bi bi-arrow-right' />
                      </div>
                      <div className='meeting-type-migration-banner__cell meeting-type-migration-banner__cell--new'>{migration.newName}</div>
                      <div className='meeting-type-migration-banner__cell-filler' />
                    </div>
                  ))}
                </div>
              </div>

              <div className='d-flex flex-column gap-3'>
                <div className='meeting-type-migration-banner__customizations-title'>What will happen to your customizations?</div>

                <div className='d-flex flex-column gap-3'>
                  <div className='meeting-type-migration-banner__bullet-row meeting-type-migration-banner__bullet-row--with-action'>
                    <div className='meeting-type-migration-banner__bullet-item'>
                      <div className='meeting-type-migration-banner__bullet-icon'>
                        <i className='bi bi-dot' />
                      </div>
                      <div className='meeting-type-migration-banner__bullet-content'>
                        <p className='meeting-type-migration-banner__bullet-text mb-0'>
                          <strong>Agenda and scorecard</strong> customizations will be lost after updating.
                        </p>
                        <p className='meeting-type-migration-banner__bullet-subtext mb-0'>Back up now to save them.</p>
                      </div>
                    </div>
                    <div className='meeting-type-migration-banner__backup-btn'>
                      <Button className='button-medium' variant='grayGhost' onClick={onBackUp} loading={isBackingUp}>
                        <i className='bi bi-download me-2' />
                        Back up agenda and scorecard
                      </Button>
                    </div>
                  </div>

                  <div className='meeting-type-migration-banner__bullet-row'>
                    <div className='meeting-type-migration-banner__bullet-item'>
                      <div className='meeting-type-migration-banner__bullet-icon'>
                        <i className='bi bi-dot' />
                      </div>
                      <div className='meeting-type-migration-banner__bullet-content'>
                        <p className='meeting-type-migration-banner__bullet-text mb-0'>
                          <strong>Custom templates</strong> are saved in the Template Library.
                        </p>
                        <p className='meeting-type-migration-banner__bullet-subtext mb-0'>
                          Use them with any meeting type or recreate legacy meeting types with your previous customizations.{' '}
                          <span
                            className='meeting-type-migration-banner__learn-link'
                            role='button'
                            tabIndex={0}
                            onClick={onLearnHow}
                            onKeyDown={(e) => e.key === 'Enter' && onLearnHow()}
                          >
                            Learn how
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Button variant='primary' className='button-medium' onClick={onUpdateAll}>
                  <i className='bi bi-arrow-up-circle me-2' />
                  Update all meeting types
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Col>
    </Row>
  )
}
